import React from "react";
import {
  RmFieldDetailsFragment,
  useRmFieldAddLocalLookupValueMutation,
  useRmFieldRemoveLocalLookupValueMutation,
} from "../../graphql/generated";
import { NotificationContext } from "../../common/context/notification";
import { Menu, Transition } from "@headlessui/react";
import {
  EllipsisVerticalIcon,
  ExclamationTriangleIcon,
  MinusIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";
import { classNames } from "../../common/utils/classnames";
import { ZenDialog, ZenDialogState } from "../../common/components/zen-dialog";

const LookupDetailsActions: React.FC<{
  field?: RmFieldDetailsFragment;
  local?: boolean;
}> = ({ field }) => {
  if (!field) {
    return <></>;
  }

  const { notifier } = React.useContext(NotificationContext);
  const [addValueDialogIsVisible, setAddValueDialogIsVisible] =
    React.useState(false);
  const [removeValueDialogIsVisible, setRemoveValueDialogIsVisible] =
    React.useState(false);

  const [{ fetching: addLookupValueFetching }, addLookupValueMutation] =
    useRmFieldAddLocalLookupValueMutation();

  const [{ fetching: removeLookupValueFetching }, removeLookupValueMutation] =
    useRmFieldRemoveLocalLookupValueMutation();

  const addLookupValueCallback = React.useCallback(
    async (value: string) => {
      await addLookupValueMutation({
        fieldId: field.id,
        lookupValue: value,
      }).then(notifier.notifyGraphql("Lookup value added"));
      setAddValueDialogIsVisible(false);
    },
    [field, addLookupValueMutation, setAddValueDialogIsVisible]
  );

  const removeLookupValueCallback = React.useCallback(
    async (value: string) => {
      await removeLookupValueMutation({
        fieldId: field.id,
        lookupValue: value,
      }).then(notifier.notifyGraphql("Lookup value removed"));
      setRemoveValueDialogIsVisible(false);
    },
    [field, removeLookupValueMutation, setRemoveValueDialogIsVisible]
  );

  const hasLocalLookupValues =
    field.localLookupValues && field.localLookupValues.length > 0;

  return (
    <>
      <Menu
        as="div"
        className="relative inline-block text-left mt-4 sm:mt-0 z-50"
      >
        <div>
          <Menu.Button className="bg-gray-100 rounded-full flex items-center text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-indigo-500">
            <span className="sr-only">Open options</span>
            <EllipsisVerticalIcon className="h-5 w-5" aria-hidden="true" />
          </Menu.Button>
        </div>

        <Transition
          as={React.Fragment}
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <Menu.Items className="origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 divide-y divide-gray-100 focus:outline-none">
            <div className="py-1">
              <Menu.Item>
                {({ active }) => (
                  <div
                    className={classNames(
                      active ? "bg-gray-100 text-gray-900" : "text-gray-700",
                      "group flex items-center px-4 py-2 text-sm cursor-pointer"
                    )}
                    onClick={() => setAddValueDialogIsVisible(true)}
                  >
                    <PlusIcon
                      className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500"
                      aria-hidden="true"
                    />
                    Add value...
                  </div>
                )}
              </Menu.Item>
              <Menu.Item disabled={!hasLocalLookupValues}>
                {({ active, disabled }) => (
                  <div
                    className={classNames(
                      disabled
                        ? "text-gray-400 cursor-not-allowed"
                        : active
                        ? "bg-gray-100 text-gray-900 cursor-pointer"
                        : "text-gray-700 cursor-pointer",
                      "group flex items-center px-4 py-2 text-sm"
                    )}
                    onClick={() =>
                      !disabled && setRemoveValueDialogIsVisible(true)
                    }
                  >
                    <MinusIcon
                      className={classNames(
                        "mr-3 h-5 w-5",
                        disabled
                          ? "text-gray-300"
                          : "text-gray-400 group-hover:text-gray-500"
                      )}
                      aria-hidden="true"
                    />
                    Remove value...
                  </div>
                )}
              </Menu.Item>
            </div>
          </Menu.Items>
        </Transition>
      </Menu>
      <AddValueDialog
        show={addValueDialogIsVisible}
        onSubmit={addLookupValueCallback}
        onCancel={() => setAddValueDialogIsVisible(false)}
        fetching={addLookupValueFetching}
      />
      <RemoveValueDialog
        show={removeValueDialogIsVisible}
        values={field.localLookupValues?.map((v) => v.value) ?? []}
        onSubmit={removeLookupValueCallback}
        onCancel={() => setRemoveValueDialogIsVisible(false)}
        fetching={removeLookupValueFetching}
      />
    </>
  );
};

const AddValueDialog: React.FC<{
  show?: boolean;
  onSubmit: (value: string) => void;
  onCancel: () => void;
  fetching?: boolean;
}> = ({ show, onSubmit, onCancel, fetching }) => {
  const [value, setValue] = React.useState("");
  const valueId = React.useId();
  const isValid = !!value.length;
  return (
    <ZenDialog
      show={show}
      title="Add lookup value"
      icon={PlusIcon}
      submit="Add"
      onSubmit={() => onSubmit(value)}
      onCancel={onCancel}
      state={
        fetching
          ? ZenDialogState.Submitting
          : isValid
          ? ZenDialogState.Displaying
          : ZenDialogState.Invalid
      }
    >
      <div className="mt-1">
        <div>
          <label htmlFor={valueId}>Value</label>
        </div>
        <div>
          <input
            id={valueId}
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
          />
        </div>
      </div>
    </ZenDialog>
  );
};

const RemoveValueDialog: React.FC<{
  show?: boolean;
  values: string[];
  onSubmit: (value: string) => void;
  onCancel: () => void;
  fetching?: boolean;
}> = ({ show, values, onSubmit, onCancel, fetching }) => {
  const [selectedValue, setSelectedValue] = React.useState("");
  const [confirmed, setConfirmed] = React.useState(false);
  const selectId = React.useId();
  const confirmId = React.useId();

  // Reset state when dialog opens
  React.useEffect(() => {
    if (show) {
      setSelectedValue("");
      setConfirmed(false);
    }
  }, [show]);

  const isValid = !!selectedValue && confirmed;

  return (
    <ZenDialog
      show={show}
      title="Remove lookup value"
      icon={ExclamationTriangleIcon}
      submit="Remove"
      onSubmit={() => onSubmit(selectedValue)}
      onCancel={onCancel}
      state={
        fetching
          ? ZenDialogState.Submitting
          : isValid
          ? ZenDialogState.Displaying
          : ZenDialogState.Invalid
      }
    >
      <div className="space-y-4">
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <ExclamationTriangleIcon
                className="h-5 w-5 text-yellow-400"
                aria-hidden="true"
              />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">
                Warning: This action has significant implications
              </h3>
              <div className="mt-2 text-sm text-yellow-700">
                <p>Before removing a lookup value, ensure that:</p>
                <ul className="list-disc list-inside mt-1 space-y-1">
                  <li>No listings currently use this lookup value</li>
                  <li>No saved searches reference this lookup value</li>
                  <li>No active mappings transform to this value</li>
                </ul>
                <p className="mt-2 font-medium">
                  Removing a lookup value that is in use may cause data
                  inconsistencies.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div>
          <label
            htmlFor={selectId}
            className="block text-sm font-medium text-gray-700"
          >
            Select value to remove
          </label>
          <select
            id={selectId}
            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
            value={selectedValue}
            onChange={(e) => setSelectedValue(e.target.value)}
          >
            <option value="">-- Select a value --</option>
            {values.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-center">
          <input
            id={confirmId}
            type="checkbox"
            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
          />
          <label
            htmlFor={confirmId}
            className="ml-2 block text-sm text-gray-900"
          >
            I understand the implications and want to proceed
          </label>
        </div>
      </div>
    </ZenDialog>
  );
};

export default LookupDetailsActions;
